import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";

type Activity = {
  id: string;
  action: string;
  targetSlug: string;
  targetTitle: string;
  actorUid: string;
  actorEmail?: string;
  createdAt?: any;
  sectionCount?: number;
  revisionId?: string;
};

function actionLabel(action: string) {
  const labels: Record<string, string> = {
    create_page_draft: "Created page draft",
    publish_page: "Published page",
    request_page_review: "Requested page review",
    approve_publish_page: "Approved and published page",
    restore_page_revision: "Restored page revision",
  };
  return labels[action] || action.replace(/_/g, " ");
}

export function PublicWebActivityPage() {
  const [items, setItems] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(query(collection(db, "publicWebActivity"), orderBy("createdAt", "desc"), limit(50)));
      setItems(snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })));
    } catch (e: any) {
      setError(e?.message || "Failed to load public website activity.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="stack">
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1>Public Website Activity</h1>
            <p className="muted">Recent CMS actions for public website pages. This gives editors a quick audit trail for content changes.</p>
          </div>
          <button className="btn secondary" onClick={() => void load()} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
        </div>
        {error ? <p className="error">{error}</p> : null}
      </div>

      <div className="card">
        {loading ? <p className="muted">Loading activity...</p> : null}
        {!loading && items.length === 0 ? <p className="muted">No public website activity has been recorded yet.</p> : null}
        <div className="list">
          {items.map((item) => (
            <div className="row" key={item.id}>
              <div>
                <b>{actionLabel(item.action)}</b>
                <div className="muted">
                  {item.targetTitle || item.targetSlug} {item.targetSlug ? <span>/{item.targetSlug}</span> : null}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : "Recent"}
                  {item.actorEmail || item.actorUid ? ` by ${item.actorEmail || item.actorUid}` : ""}
                </div>
              </div>
              {item.sectionCount !== undefined ? <span className="notice-light">{item.sectionCount} sections</span> : null}
              {item.revisionId ? <span className="notice-light">Revision restored</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
